import { create } from "zustand";

import { IHttpError } from "models/interface";
import { RankingProjectService } from "../../../services/rankingProject.service";
import {
  IRankingSnapshotHeader,
  IRankingSnapshotRow,
} from "../../../models/interface";

type SnapshotSortColumn =
  | "record"
  | "effective"
  | "through"
  | "series"
  | "rank"
  | "points";
type SnapshotSortOrder = "ascend" | "descend";

interface RankingSnapshotState {
  isLoading: boolean;
  dataFetchError: IHttpError | null;
  header: IRankingSnapshotHeader | null;
  rows: IRankingSnapshotRow[];
  sortColumn: SnapshotSortColumn | null;
  sortOrder: SnapshotSortOrder | null;
  searchTerm: string;
  fetchRankingSnapshot: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  setSort: (
    sortColumn: SnapshotSortColumn | null,
    sortOrder: SnapshotSortOrder | null,
  ) => void;
  setSearchTerm: (searchTerm: string) => void;
  getVisibleRows: () => IRankingSnapshotRow[];
  getMedalSummary: () => { rank_name: string; count: number }[];
}

// "rank" sorts by rank_display_order, where a lower display_order is the higher medal, so
// ascending order already reads highest-medal-first (spec.md FR-012). "points" sorts by the raw
// score, so descending order reads highest-points-first (spec.md FR-013).
const defaultSortForRankingType = (
  ranking_type: IRankingSnapshotHeader["ranking_type"],
): { sortColumn: SnapshotSortColumn; sortOrder: SnapshotSortOrder } =>
  ranking_type === "template"
    ? { sortColumn: "rank", sortOrder: "ascend" }
    : { sortColumn: "points", sortOrder: "descend" };

const compareRows = (
  a: IRankingSnapshotRow,
  b: IRankingSnapshotRow,
  sortColumn: SnapshotSortColumn,
): number => {
  switch (sortColumn) {
    case "record":
      return a.record_name.localeCompare(b.record_name);
    case "effective":
      return a.effective_from.localeCompare(b.effective_from);
    case "through":
      return a.through_to.localeCompare(b.through_to);
    case "series":
      return a.series_name.localeCompare(b.series_name);
    case "rank":
      return (a.rank_display_order ?? 0) - (b.rank_display_order ?? 0);
    case "points":
      return (a.points ?? 0) - (b.points ?? 0);
    default:
      return 0;
  }
};

export const useRankingSnapshotStore = create<RankingSnapshotState>(
  (set, get) => ({
    isLoading: true,
    dataFetchError: null,
    header: null,
    rows: [],
    sortColumn: null,
    sortOrder: null,
    searchTerm: "",

    fetchRankingSnapshot: async (
      project_slug: string,
      ranking_project_id: number,
    ) => {
      set(() => ({ isLoading: true }));
      try {
        const { data } = await new RankingProjectService().getRankingSnapshot(
          project_slug,
          ranking_project_id,
        );
        const header = data.data.header;
        set(() => ({
          header,
          rows: data.data.rows ?? [],
          ...(header ? defaultSortForRankingType(header.ranking_type) : {}),
          isLoading: false,
          dataFetchError: null,
        }));
      } catch (error: any) {
        set(() => ({ dataFetchError: error, isLoading: false }));
      }
    },

    setSort: (sortColumn, sortOrder) => {
      set(() => ({ sortColumn, sortOrder }));
    },

    setSearchTerm: (searchTerm: string) => {
      set(() => ({ searchTerm }));
    },

    getVisibleRows: () => {
      const { rows, sortColumn, sortOrder, searchTerm } = get();
      const term = searchTerm.trim().toLowerCase();
      const filtered = term
        ? rows.filter(
            (row) =>
              row.record_name.toLowerCase().includes(term) ||
              row.series_name.toLowerCase().includes(term),
          )
        : rows;

      if (!sortColumn || !sortOrder) {
        return filtered;
      }

      const sorted = [...filtered].sort((a, b) => compareRows(a, b, sortColumn));
      return sortOrder === "descend" ? sorted.reverse() : sorted;
    },

    // Medal counts across the full snapshot (independent of the search filter, so the summary
    // always reflects the complete result set) — only meaningful for Template-based projects,
    // where rows carry a rank_name/rank_display_order instead of a raw score.
    getMedalSummary: () => {
      const { rows, header } = get();
      if (header?.ranking_type !== "template") {
        return [];
      }
      const countByRank = new Map<string, { count: number; order: number }>();
      rows.forEach((row) => {
        if (!row.rank_name) {
          return;
        }
        const existing = countByRank.get(row.rank_name);
        countByRank.set(row.rank_name, {
          count: (existing?.count ?? 0) + 1,
          order: row.rank_display_order ?? existing?.order ?? 0,
        });
      });
      return Array.from(countByRank.entries())
        .map(([rank_name, { count, order }]) => ({ rank_name, count, order }))
        .sort((a, b) => a.order - b.order);
    },
  }),
);
